import { useEffect, type RefObject } from 'react';
import { accentTokens, isHexColor } from './accent';
import {
  anyClips,
  bleedFor,
  headroomFor,
  overflowChain,
  riseFor,
  topBoxes,
  topFixedBottom,
  useIsomorphicLayoutEffect,
} from './bleed';
import { chromeVarsFor, paintChrome } from './chrome';
import { paintScrollbar } from './scrollbar';

/**
 * EVERYTHING AN EVENT PAGE DOES TO THE PAGE AROUND IT, FOR A SERVE PAGE.
 *
 * The same three things the event-details widget does, lifted out of
 * EventDetails.tsx so an opportunity, a Collection and a care page get them
 * too:
 *
 *   1. The hero reaches past its column to the screen edges and up under the
 *      site's header (measured, never `100vw`: see bleed.ts). The root gets
 *      --ed-bleed-l/-r/-t and --ed-headroom; `.bleed`, `.bleed-inner` and
 *      `.bleed-headroom` in globals.css use them.
 *   2. While Salient's header is transparent over that hero, it wears the
 *      page's colour (chrome.ts). No colour, nothing written.
 *   3. The scrollbar says the page is light, tinted where there is a colour.
 *
 * `ready` is whatever flips when the hero is on the page, so the measuring
 * runs once it exists. `enabled` false (a widget embedded in a section of
 * someone else's page) leaves the page alone entirely.
 */
export function useEventChrome(
  rootRef: RefObject<HTMLElement | null>,
  accent: string | null | undefined,
  accentInk: string | null | undefined,
  ready: unknown,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;
    const vars = isHexColor(accent)
      ? chromeVarsFor(accent, accentInk ?? null, accentTokens(accent, accentInk).onAccent)
      : null;
    return paintChrome(document, vars);
  }, [accent, accentInk, enabled]);

  useEffect(() => (enabled ? paintScrollbar(document, accent) : undefined), [accent, enabled]);

  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return;
    let lastWidths = '';
    const measure = () => {
      const page = document.documentElement.clientWidth;
      const mine = Math.round(root.getBoundingClientRect().width);
      const widths = `${page}:${mine}`;
      if (widths === lastWidths) return;
      lastWidths = widths;
      if (anyClips(overflowChain(root))) {
        for (const name of ['--ed-bleed-l', '--ed-bleed-r', '--ed-bleed-t', '--ed-headroom'])
          root.style.removeProperty(name);
        return;
      }
      root.style.setProperty('--ed-bleed-t', '0px');
      root.style.setProperty('--ed-headroom', '0px');
      const rect = root.getBoundingClientRect();
      const body = document.body.getBoundingClientRect();
      const { left, right } = bleedFor(rect, {
        left: Math.max(0, body.left),
        right: Math.min(page, body.right),
      });
      const rise = riseFor(topBoxes(root), rect.top);
      const heroTop = rect.top - rise;
      const headroom = headroomFor(topFixedBottom(document, heroTop), heroTop);
      root.style.setProperty('--ed-bleed-l', `${left}px`);
      root.style.setProperty('--ed-bleed-r', `${right}px`);
      root.style.setProperty('--ed-bleed-t', `${rise}px`);
      root.style.setProperty('--ed-headroom', `${headroom}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(document.documentElement);

    // A tab restored from the background measures mid-animation and reads no
    // header; forget the last widths and measure again once it settles.
    let pending: number[] = [];
    const remeasure = () => {
      for (const id of pending) window.clearTimeout(id);
      pending = [0, 250].map((wait) =>
        window.setTimeout(() => {
          lastWidths = '';
          measure();
        }, wait)
      );
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) remeasure();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') remeasure();
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      observer.disconnect();
      for (const id of pending) window.clearTimeout(id);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ready, enabled]);
}
